import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { Variables } from "../../Variations/Variations";
import { toast } from "react-toastify";
import { AiOutlineRight } from "react-icons/ai";

const ManageContent = () => {
  const { ContentID } = useParams();
  const navigate = useNavigate();
  const contentIdNum = Number(ContentID) || 0;

  // ---- State (matches API JSON) ----
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [buttonUrl, setButtonUrl] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [widgetId, setWidgetId] = useState(null); // number|null
  const [isPublished, setIsPublished] = useState(false);
  const [titleAr, setTitleAr] = useState("");
  const [bodyAr, setBodyAr] = useState("");
  const [buttonTextAr, setButtonTextAr] = useState("");

  // ---- Widgets for select ----
  const [widgets, setWidgets] = useState([]);
  // Build options from API data
  const widgetOptions = (Array.isArray(widgets) ? widgets : [])
    .filter((w) => parseInt(w.WidgetTypeId) === 1)
    .map((w) => ({
      value: w.Id,
      label: w.DisplayName ?? w.Name ?? String(w.Id),
    }));

  // Selected option from ID
  const selectedWidgetOption =
    widgetOptions.find((o) => o.value === widgetId) || null;

  // ---- Refs ----
  const fileInputRef = useRef(null);
  // put these above handleSubmit (anywhere in the component file)
  const isBlank = (v) => typeof v !== "string" || v.trim() === "";

  // ---- Loaders ----
  async function loadWidgets() {
    try {
      const res = await fetch(`${Variables.API_URL}Rasha/Widget/Get`);
      if (res.ok) setWidgets(await res.json());
      else toast.error("Failed to load widgets");
    } catch {
      toast.error("Failed to load widgets");
    }
  }

  async function loadContent() {
    try {
      const res = await fetch(
        `${Variables.API_URL}Rasha/Content/Get/${contentIdNum}`
      );
      if (!res.ok) return;
      const data = await res.json();
      console.log(JSON.stringify(data));

      // Accept camelCase or PascalCase
      setTitle(data.Content.title ?? data.Content.Title ?? "");
      setBody(data.Content.body ?? data.Content.Body ?? "");
      setButtonUrl(data.Content.buttonUrl ?? data.Content.ButtonUrl ?? "");
      setButtonText(data.Content.buttonText ?? data.Content.ButtonText ?? "");
      setImageUrl(data.Content.imageUrl ?? data.Content.ImageUrl ?? "");
      setWidgetId(data.Content.widgetId ?? data.Content.WidgetId ?? null);
      setIsPublished(
        typeof data.Content.isPublished === "boolean"
          ? data.Content.isPublished
          : Boolean(data.Content.IsPublished)
      );
      setTitleAr(data.Content.titleAr ?? data.Content.TitleAr ?? "");
      setBodyAr(data.Content.bodyAr ?? data.Content.BodyAr ?? "");
      setButtonTextAr(
        data.Content.buttonTextAr ?? data.Content.ButtonTextAr ?? ""
      );
    } catch {
      toast.error("Failed to load content");
    }
  }

  useEffect(() => {
    loadWidgets();
    if (contentIdNum !== 0) loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentIdNum]);

  // ---- Normal <input type="file"> upload (<= 2MB) ----
  const onFileInputChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size >= 2_000_000) {
      toast.error("Image is not accepted (max 2MB).");
      e.target.value = "";
      return;
    }

    const formdata = new FormData();
    formdata.append("Images", file);

    const requestOptions = {
      method: "POST",
      body: formdata,
      redirect: "follow",
    };

    fetch(`${Variables.API_URL}UploadMedia/UploadImageToServer`, requestOptions)
      .then((response) => response.json())
      .then((result) => setImageUrl(result[0]))
      .catch((error) => console.error(error));
  };

  // ---- Validation ----
  function isValid() {
    if (isBlank(title)) {
      toast.error("Title is required");
      return false;
    }
    if (isBlank(body)) {
      toast.error("Body is required");
      return false;
    }
    if (isBlank(buttonText)) {
      toast.error("Button Text is required");
      return false;
    }
    if (isBlank(buttonUrl)) {
      toast.error("Button URL is required");
      return false;
    }
    if (isBlank(imageUrl)) {
      toast.error("Image is required (upload an image)");
      return false;
    }
    if (!widgetId || Number(widgetId) <= 0) {
      toast.error("Widget is required");
      return false;
    }
    if (isBlank(titleAr)) {
      toast.error("Title (Arabic) is required");
      return false;
    }
    if (isBlank(bodyAr)) {
      toast.error("Body (Arabic) is required");
      return false;
    }
    if (isBlank(buttonTextAr)) {
      toast.error("Button Text (Arabic) is required");
      return false;
    }

    return true;
  }
  // ---- Save (Add / Update) ----
  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid()) return;

    try {
      let payload;
      if (contentIdNum === 0) {
        payload = {
          title,
          body,
          buttonUrl,
          buttonText,
          imageUrl,
          widgetId, // number
          isPublished,
          titleAr,
          bodyAr,
          buttonTextAr,
        };
      } else {
        payload = {
          id: contentIdNum,
          title,
          body,
          buttonUrl,
          buttonText,
          imageUrl,
          widgetId, // number
          isPublished,
          titleAr,
          bodyAr,
          buttonTextAr,
        };
      }
      console.log(payload)

      const res = await fetch(`${Variables.API_URL}Rasha/Content/Upsert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(await res.text());

      toast.success(
        contentIdNum === 0
          ? "Content added successfully"
          : "Content updated successfully"
      );
      navigate("/admin/rasha/Contents");
    } catch (err) {
      toast.error(err.message || "Failed to save");
    }
  }

  // ---- Render ----
  return (
    <div className="rightSide">
      <div className="container-fluid mt-3">
        {/* Header */}
        <div className="row mb-3">
          <div className="col-12">
            <h2>
              {contentIdNum === 0 ? "Add" : `Manage ${title || "Content"}`}{" "}
              Content
            </h2>
            <ul className="portalPath">
              <li>
                Home <AiOutlineRight />
              </li>
              <li>
                <Link to="/admin/rasha/Contents">
                  Contents <AiOutlineRight />
                </Link>
              </li>
              <li>{contentIdNum === 0 ? "Add" : "Manage"} Content</li>
            </ul>
          </div>
        </div>

        {/* Form */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-end mb-3">
                  <NavLink
                    to="/admin/rasha/Contents"
                    className="btn btn-secondary me-2"
                  >
                    Cancel
                  </NavLink>
                  <button className="btn btn-primary" onClick={handleSubmit}>
                    Save
                  </button>
                </div>

                <div className="row gy-3">
                  {/* Title / TitleAr */}
                  <div className="col-lg-6">
                    <label className="mb-2">
                      Title <span className="required">*</span>
                    </label>
                    <input
                      id="title"
                      className="form-control"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Title"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="mb-2">Title (Arabic)</label>
                    <input
                      id="titleAr"
                      className="form-control"
                      value={titleAr}
                      onChange={(e) => setTitleAr(e.target.value)}
                      placeholder="Title in Arabic"
                    />
                  </div>

                  {/* Body / BodyAr */}
                  <div className="col-lg-6">
                    <label className="mb-2">Body</label>
                    <textarea
                      id="body"
                      className="form-control"
                      rows={5}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Content body"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="mb-2">Body (Arabic)</label>
                    <textarea
                      id="bodyAr"
                      className="form-control"
                      rows={5}
                      value={bodyAr}
                      onChange={(e) => setBodyAr(e.target.value)}
                      placeholder="Content body in Arabic"
                    />
                  </div>

                  {/* Button fields */}
                  <div className="col-lg-6">
                    <label className="mb-2">Button Text</label>
                    <input
                      id="buttonText"
                      className="form-control"
                      value={buttonText}
                      onChange={(e) => setButtonText(e.target.value)}
                      placeholder="e.g., Learn more"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="mb-2">Button Text (Arabic)</label>
                    <input
                      id="buttonTextAr"
                      className="form-control"
                      value={buttonTextAr}
                      onChange={(e) => setButtonTextAr(e.target.value)}
                      placeholder="Arabic button text"
                    />
                  </div>

                  <div className="col-lg-12">
                    <label className="mb-2">Button URL</label>
                    <input
                      id="buttonUrl"
                      className="form-control"
                      value={buttonUrl}
                      onChange={(e) => setButtonUrl(e.target.value)}
                      placeholder="Button Url"
                    />
                  </div>

                  {/* Publish toggle */}
                  <div className="form-group col-lg-3 published-check d-flex align-items-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="publish"
                      checked={isPublished}
                      onChange={(e) => setIsPublished(e.target.checked)}
                    />
                    &nbsp;
                    <label className="form-check-label" htmlFor="publish">
                      Publish
                    </label>
                  </div>

                  {/* Widget select */}
                  <div className="col-lg-6">
                    <label className="mb-2">
                      Widget <span className="required">*</span>
                    </label>
                    <Select
                      options={widgetOptions}
                      value={selectedWidgetOption}
                      onChange={(opt) => setWidgetId(opt ? opt.value : null)}
                      placeholder="Select a widget…"
                      isClearable
                    />
                  </div>

                  {/* Image uploader (normal input) */}
                  <div className="col-lg-6">
                    <label className="mb-2">Image (max 2MB)</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="form-control"
                      onChange={onFileInputChange}
                    />
                    {imageUrl && (
                      <div className="mt-3">
                        <img
                          src={Variables.API_PUBLISHED + imageUrl}
                          alt="preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: 200,
                            display: "block",
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger mt-2"
                          onClick={() => setImageUrl("")}
                        >
                          Remove Image
                        </button>
                        <small className="text-muted d-block mt-2">
                          Uploaded URL: {imageUrl}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageContent;
